import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";

export interface TotalRankUser {
  id: string;
  nickname: string;
  points: number;
  postCount: number;
  attendanceCount: number;
  totalScore: number;
  rank: number;
}

const POST_WEIGHT = 10;
const ATTENDANCE_WEIGHT = 3;

interface UseTotalRankingReturn {
  rankings: TotalRankUser[];
  loading: boolean;
  error: string | null;
  refreshRanking: () => Promise<void>;
}

export function useTotalRanking(limit: number = 10): UseTotalRankingReturn {
  const [rankings, setRankings] = useState<TotalRankUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRanking = useCallback(async () => {
    try {
      setError(null);

      const { data: profiles, error: profileError } = await supabase
        .from("profiles")
        .select("id, nickname, points")
        .limit(limit * 3);

      if (profileError) throw profileError;
      if (!profiles || profiles.length === 0) {
        setRankings([]);
        return;
      }

      const userIds = profiles.map((p) => p.id);

      const { data: postCounts, error: postError } = await supabase
        .from("posts")
        .select("author_id")
        .in("author_id", userIds);

      if (postError) throw postError;

      const postCountMap: Record<string, number> = {};
      (postCounts || []).forEach((p) => {
        postCountMap[p.author_id] = (postCountMap[p.author_id] || 0) + 1;
      });

      const { data: attendanceRecords, error: attError } = await supabase
        .from("attendances")
        .select("user_id")
        .in("user_id", userIds);

      if (attError) throw attError;

      const attendanceCountMap: Record<string, number> = {};
      (attendanceRecords || []).forEach((a) => {
        attendanceCountMap[a.user_id] = (attendanceCountMap[a.user_id] || 0) + 1;
      });

      const scored: TotalRankUser[] = profiles.map((profile) => {
        const postCount = postCountMap[profile.id] || 0;
        const attendanceCount = attendanceCountMap[profile.id] || 0;
        const totalScore =
          (profile.points ?? 0) +
          postCount * POST_WEIGHT +
          attendanceCount * ATTENDANCE_WEIGHT;

        return {
          id: profile.id,
          nickname: profile.nickname || "익명",
          points: profile.points ?? 0,
          postCount,
          attendanceCount,
          totalScore,
          rank: 0,
        };
      });

      scored.sort((a, b) => b.totalScore - a.totalScore);
      const top = scored.slice(0, limit).map((user, idx) => ({
        ...user,
        rank: idx + 1,
      }));

      setRankings(top);
    } catch {
      setError("총랭킹을 불러오지 못했습니다");
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchRanking();
  }, [fetchRanking]);

  const refreshRanking = useCallback(async () => {
    setLoading(true);
    await fetchRanking();
  }, [fetchRanking]);

  return { rankings, loading, error, refreshRanking };
}